import Popup from '../../../../../../common/ui/popup';
import { useFoodStore } from '../../../../data/food-store';
import { format } from 'date-fns';
import { MDXEditor, MDXEditorMethods } from '@mdxeditor/editor';
import { getCompactMarkdownEditorPlugins } from '../../../../../../common/markdown/markdown-editor-provider';
import { useEffect, useRef, useState } from 'react';
import { PreloaderIcon } from '../../../../../../common/ui/icons';
import { deleteFoodDayMetadata, upsertFoodDayMetadata } from '../../../../data/food-data-service';
import { showToast } from '../../../../../../common/domain/toast-provider';
import { endOfYear, startOfYear } from 'date-fns';
import { createFoodCalendar } from '../../../../domain/food-calendar-provider';

interface IDayMetadataPopup {
    onClose: () => void;
}

const DayMetadataPopup = ({ onClose }: IDayMetadataPopup) => {

    const dayPopupMetadata = useFoodStore(store => store.dayPopupMetadata);
    const currentCalendarYear = useFoodStore(store => store.currentCalendarYear);
    const setFoodCalendarData = useFoodStore(store => store.setFoodCalendarData);

    const editorRef = useRef<MDXEditorMethods>(null);

    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        if(!editorRef?.current) return;
        editorRef.current?.setMarkdown(dayPopupMetadata?.description ?? '');
    }, [dayPopupMetadata?.description]);

    const saveMetadata = async () => {

        setLoading(true);

        const description = editorRef.current?.getMarkdown() || '';
        const date = new Date(currentCalendarYear, 0, 1); // January is 0
        const fromDate = startOfYear(date).getTime();
        const toDate = endOfYear(date).getTime();

        const response = await upsertFoodDayMetadata(
            dayPopupMetadata?.day_date ?? 0,
            description,
            fromDate,
            toDate
        );

        setLoading(false);

        if(!response) {
            showToast('An error has occured.');
            return;
        }

        const result = createFoodCalendar(date.getFullYear(), response);
        setFoodCalendarData(result);

        onClose();
    };

    const deleteMetadata = async () => {

        if(!dayPopupMetadata?.id) {
            onClose();
            return;
        }

        setLoading(true);

        const date = new Date(currentCalendarYear, 0, 1); // January is 0
        const fromDate = startOfYear(date).getTime();
        const toDate = endOfYear(date).getTime();

        const response = await deleteFoodDayMetadata(
            dayPopupMetadata.id,
            fromDate,
            toDate,
        );

        setLoading(false);

        if(!response) {
            showToast('Delete day metadata error.');
            return;
        }

        const result = createFoodCalendar(date.getFullYear(), response);
        setFoodCalendarData(result);

        onClose();
    };

    return (
        <Popup onClose={ onClose } width={ 650 } height={ 350 }>
            <div className="p-4 flex flex-col overflow-auto text-left font-normal text-gray-900">

                <h1 className="text-2xl text-slate-800 mb-4">
                    Metadata for { format(dayPopupMetadata.day_date, 'dd MMM, yyyy') }
                </h1>

                <div className="markdown-editor border rounded-lg mb-4">
                    <MDXEditor
                        ref={ editorRef }
                        markdown={ '' }
                        plugins={ getCompactMarkdownEditorPlugins() }
                    />
                </div>

                <div className="flex items-center justify-end text-sm mt-4">
                    <button
                        className="bg-stone-400 text-slate-100 rounded px-6 py-2 mr-2"
                        onClick={ onClose }
                        type="button">Close</button>

                    {
                        isLoading &&
                        <PreloaderIcon size={ 24 } color={ '#717985' } />
                    }

                    {
                        !isLoading &&
                        <button
                            className="bg-red-500 text-red-100 rounded px-6 py-2 mr-2"
                            onClick={ deleteMetadata }
                            type="button">Delete</button>
                    }

                    {
                        !isLoading &&
                        <button
                            className="bg-slate-500 text-slate-100 rounded px-6 py-2"
                            onClick={ saveMetadata }
                            type="button">Save</button>
                    }

                </div>

            </div>
        </Popup>
    )
};

export default DayMetadataPopup;